import React, { useRef, useState, useEffect } from "react";
import Axios from "axios";
import { toast, ToastContainer } from "react-toastify";

const ALLOWED_LOGO_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
];
const MAX_LOGO_SIZE = 500 * 1024; // 500 KB
const MIN_QUOTE_LENGTH = 20;
const MAX_QUOTE_LENGTH = 400;

const ManageClients = () => {
  const [showModal, setShowModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [clientName, setClientName] = useState("");
  const [clientRole, setClientRole] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [clientQuote, setClientQuote] = useState("");
  const [clientLogo, setClientLogo] = useState(null);
  const [currentLogo, setCurrentLogo] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [pendingClients, setPendingClients] = useState([]);
  const [errors, setErrors] = useState({});

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const formRef = useRef(null);

  const [createdAt, setCreatedAt] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");

  // ── Drag & drop reordering state ──────────────────────────
  // const [draggedIndex, setDraggedIndex] = useState(null);
  // const [dragOverIndex, setDragOverIndex] = useState(null);
  // const [savingOrder, setSavingOrder] = useState(false);

  const selectedTestimonial =
    clients.find((client) => client.id === editingId) || null;

  useEffect(() => {
    displayClients();
    loadPendingClients();
  }, []);

  const clearFieldError = (field) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!clientName.trim()) {
      newErrors.clientName = "Client name is required.";
    } else if (clientName.trim().length < 2) {
      newErrors.clientName = "Client name must be at least 2 characters.";
    }

    if (!clientRole.trim()) {
      newErrors.clientRole = "Role / designation is required.";
    }

    if (!companyName.trim()) {
      newErrors.companyName = "Company name is required.";
    }

    if (!industry.trim()) {
      newErrors.industry = "Industry is required.";
    }

    if (!clientQuote.trim()) {
      newErrors.clientQuote = "Please enter the testimonial.";
    } else if (clientQuote.trim().length < MIN_QUOTE_LENGTH) {
      newErrors.clientQuote = `Testimonial must be at least ${MIN_QUOTE_LENGTH} characters.`;
    } else if (clientQuote.length > MAX_QUOTE_LENGTH) {
      newErrors.clientQuote = `Testimonial cannot exceed ${MAX_QUOTE_LENGTH} characters.`;
    }

    // A logo is only required when adding, or when editing without an
    // existing logo already on file. Editing can keep the current logo.
    const hasExistingLogo = Boolean(editingId && currentLogo);

    if (!clientLogo && !hasExistingLogo) {
      newErrors.clientLogo = "Please choose a logo.";
    } else if (clientLogo) {
      if (!ALLOWED_LOGO_TYPES.includes(clientLogo.type)) {
        newErrors.clientLogo = "Only JPG, JPEG, PNG & WEBP files are allowed.";
      } else if (clientLogo.size > MAX_LOGO_SIZE) {
        newErrors.clientLogo = "Image size must be less than 500 KB.";
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const uploadClient = async () => {
    if (!validateForm()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("client_name", clientName);
      formData.append("client_role", clientRole);
      formData.append("company_name", companyName);
      formData.append("industry", industry);
      formData.append("client_quote", clientQuote);
      formData.append("client_logo", clientLogo);

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/add_client.php`,
        formData,
      );

      if (res.data.success) {
        toast.success("Client testimonial added successfully.");

        setClientName("");
        setClientRole("");
        setCompanyName("");
        setIndustry("");
        setClientQuote("");
        setClientLogo(null);

        document.getElementById("clientLogo").value = "";

        clearForm();
        setShowModal(false);
        displayClients();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const displayClients = async () => {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_BASE_URL}php/get_clients.php`,
      );

      if (res.data.success) {
        setClients(res.data.clients);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const editClient = (client) => {
    setEditingId(client.id);

    setClientName(client.client_name);
    setClientRole(client.client_role);
    setCompanyName(client.company_name);
    setIndustry(client.industry);
    setClientQuote(client.client_quote);
    setClientLogo(null);
    setCurrentLogo(client.client_logo);

    const fileInput = document.getElementById("clientLogo");
    if (fileInput) {
      fileInput.value = "";
    }

    setErrors({});

    formRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);

    setClientName("");
    setClientRole("");
    setCompanyName("");
    setIndustry("");
    setClientQuote("");
    setClientLogo(null);

    const fileInput = document.getElementById("clientLogo");

    if (fileInput) {
      fileInput.value = "";
    }

    clearForm();
  };

  const clearForm = () => {
    setEditingId(null);

    setClientName("");
    setClientRole("");
    setCompanyName("");
    setIndustry("");
    setClientQuote("");
    setClientLogo(null);
    setErrors({});

    const fileInput = document.getElementById("clientLogo");

    if (fileInput) {
      fileInput.value = "";
    }
  };

  const updateClient = async () => {
    if (!validateForm()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();

      formData.append("id", editingId);
      formData.append("client_name", clientName);
      formData.append("client_role", clientRole);
      formData.append("company_name", companyName);
      formData.append("industry", industry);
      formData.append("client_quote", clientQuote);

      if (clientLogo) {
        formData.append("client_logo", clientLogo);
      }

      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/update_client.php`,
        formData,
      );

      if (res.data.success) {
        toast.success("Client updated successfully.");

        setEditingId(null);
        setClientName("");
        setClientRole("");
        setCompanyName("");
        setIndustry("");
        setClientQuote("");
        setClientLogo(null);

        document.getElementById("clientLogo").value = "";

        clearForm();
        setShowModal(false);
        displayClients();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm("Delete this client testimonial?")) return;

    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/delete_client.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Client deleted successfully.");
        displayClients();
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      console.log(err);
      toast.error("Delete failed.");
    }
  };

  const loadPendingClients = async () => {
    const res = await Axios.get(
      `${process.env.REACT_APP_API_BASE_URL}php/get_pending_clients.php`,
    );

    if (res.data.success) {
      setPendingClients(res.data.clients);
    }
  };

  const approveClient = async (id) => {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/approve_client.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Testimonial approved.");

        loadPendingClients();
        displayClients();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const rejectClient = async (id) => {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_BASE_URL}php/reject_client.php`,
        { id },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (res.data.success) {
        toast.success("Testimonial rejected.");

        loadPendingClients();
        displayClients();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/client-testimonial`;

    navigator.clipboard.writeText(url);

    toast.success("Form link copied!");
  };

  // ── Modal + read-more helpers ─────────────────────────────
  const openAddModal = () => {
    cancelEdit();
    setShowModal(true);
  };

  const openEditModal = (client) => {
    editClient(client);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    cancelEdit();
  };

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ── Drag & drop reordering handlers ───────────────────────
  // const handleDragStart = (e, index) => {
  //   if (savingOrder) return;
  //   setDraggedIndex(index);
  //   e.dataTransfer.effectAllowed = "move";
  //   // Firefox requires setData to be called for drag to initiate
  //   e.dataTransfer.setData("text/plain", String(index));
  // };

  // const handleDragOver = (e, index) => {
  //   e.preventDefault();
  //   e.dataTransfer.dropEffect = "move";
  //   if (index !== dragOverIndex) {
  //     setDragOverIndex(index);
  //   }
  // };

  // const handleDragLeave = () => {
  //   setDragOverIndex(null);
  // };

  // const handleDrop = (e, dropIndex) => {
  //   e.preventDefault();

  //   if (draggedIndex === null || draggedIndex === dropIndex) {
  //     setDraggedIndex(null);
  //     setDragOverIndex(null);
  //     return;
  //   }

  //   const reordered = [...clients];
  //   const [movedClient] = reordered.splice(draggedIndex, 1);
  //   reordered.splice(dropIndex, 0, movedClient);

  //   setClients(reordered);
  //   setDraggedIndex(null);
  //   setDragOverIndex(null);

  //   saveOrder(reordered);
  // };

  // const handleDragEnd = () => {
  //   setDraggedIndex(null);
  //   setDragOverIndex(null);
  // };

  // const saveOrder = async (orderedClients) => {
  //   try {
  //     setSavingOrder(true);

  //     const order = orderedClients.map((client, index) => ({
  //       id: client.id,
  //       sort_order: index,
  //     }));

  //     const res = await Axios.post(
  //       `${process.env.REACT_APP_API_BASE_URL}php/reorder_clients.php`,
  //       { order },
  //       {
  //         headers: {
  //           "Content-Type": "application/json",
  //         },
  //       },
  //     );

  //     if (res.data.success) {
  //       toast.success("Order updated.");
  //     } else {
  //       toast.error(res.data.message || "Failed to save order.");
  //       displayClients(); // revert to last saved order on failure
  //     }
  //   } catch (err) {
  //     console.log(err);
  //     toast.error("Failed to save order.");
  //     displayClients(); // revert to last saved order on failure
  //   } finally {
  //     setSavingOrder(false);
  //   }
  // };

  return (
    <>
      <div className="twm-right-section-panel site-bg-gray">
        <div className="panel panel-default">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 px-4">
            <h4 className="mb-0">Manage Client Testimonials</h4>

            <div className="d-flex gap-2">
              <button className="site-button px-4 py-2" onClick={copyLink}>
                <i className="fa fa-copy me-1"></i>
                Copy Form Link
              </button>

              <button
                className="site-button px-4 py-2"
                // style={{ backgroundColor: "#2ecc71", borderColor: "#2ecc71" }}
                onClick={openAddModal}
              >
                <i className="fa fa-plus me-1"></i>
                Add Testimonial
              </button>
            </div>
          </div>

          <div className="panel-body p-a30">
            {/* ================= PENDING SECTION ================= */}
            {pendingClients.length > 0 && (
              <section className="pending-clients-section">
                <div
                  className="card mb-4"
                  style={{
                    border: "1px solid #f5c76b",
                    borderRadius: "10px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center px-3 py-2"
                    style={{
                      backgroundColor: "#fff6e6",
                      borderBottom: "1px solid #f5c76b",
                    }}
                  >
                    <h5 className="mb-0" style={{ color: "#8a5a00" }}>
                      <i className="fa fa-clock-o me-2"></i>
                      Pending Testimonial Requests
                    </h5>
                    <span
                      className="badge"
                      style={{
                        backgroundColor: "#f5a623",
                        color: "#fff",
                        fontSize: "0.85rem",
                      }}
                    >
                      {pendingClients.length}
                    </span>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead style={{ backgroundColor: "#fdf1dc" }}>
                        <tr>
                          <th style={{ width: "70px" }}>Logo</th>
                          <th>Client</th>
                          <th>Company</th>
                          <th>Industry</th>
                          <th>Role</th>
                          <th style={{ minWidth: "260px" }}>Testimonial</th>
                          <th
                            style={{ width: "100px" }}
                            className="text-center"
                          >
                            Action
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {pendingClients.length === 0 ? (
                          <tr>
                            <td
                              colSpan="7"
                              className="text-center text-muted py-4"
                            >
                              No pending testimonials.
                            </td>
                          </tr>
                        ) : (
                          pendingClients.map((client) => {
                            const isExpanded = !!expandedRows[client.id];
                            return (
                              <tr key={client.id}>
                                <td>
                                  <img
                                    src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/clients/${client.client_logo}`}
                                    alt={client.client_name}
                                    width="50"
                                    height="50"
                                    style={{
                                      borderRadius: "8px",
                                      objectFit: "cover",
                                    }}
                                  />
                                </td>

                                <td>
                                  <strong>{client.client_name}</strong>
                                </td>

                                <td>{client.company_name}</td>
                                <td>{client.industry}</td>
                                <td>{client.client_role}</td>

                                <td style={{ maxWidth: "320px" }}>
                                  <p
                                    className="mb-1"
                                    style={
                                      isExpanded
                                        ? { margin: 0 }
                                        : {
                                            display: "-webkit-box",
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                            margin: 0,
                                          }
                                    }
                                  >
                                    {client.client_quote}
                                  </p>
                                  {client.client_quote &&
                                    client.client_quote.length > 120 && (
                                      <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0"
                                        onClick={() => toggleExpand(client.id)}
                                      >
                                        {isExpanded ? "Read less" : "Read more"}
                                      </button>
                                    )}
                                </td>

                                <td>
                                  <div className="d-flex justify-content-center gap-2">
                                    <button
                                      className="btn btn-success btn-sm"
                                      title="Approve"
                                      onClick={() => approveClient(client.id)}
                                    >
                                      <i className="fa fa-check"></i>
                                      <span className="visually-hidden">
                                        Approve
                                      </span>
                                    </button>

                                    <button
                                      className="btn btn-danger btn-sm"
                                      title="Reject"
                                      onClick={() => rejectClient(client.id)}
                                    >
                                      <i className="fa fa-times"></i>
                                      <span className="visually-hidden">
                                        Reject
                                      </span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* ================= APPROVED SECTION ================= */}
            <div
              className="card"
              style={{
                border: "1px solid #dcdfe3",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center px-3 py-2"
                style={{
                  backgroundColor: "#f4f6f8",
                  borderBottom: "1px solid #dcdfe3",
                }}
              >
                <h5 className="mb-0">
                  <i
                    className="fa fa-check-circle me-2"
                    style={{ color: "#2ecc71" }}
                  ></i>
                  Published Testimonials
                </h5>
                <div className="d-flex align-items-center gap-2">
                  {/* {savingOrder && (
                    <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                      Saving order...
                    </span>
                  )} */}
                  <span
                    className="badge"
                    style={{
                      backgroundColor: "#4a90e2",
                      color: "#fff",
                      fontSize: "0.85rem",
                    }}
                  >
                    {clients.length}
                  </span>
                </div>
              </div>

              {/* {clients.length > 1 && (
                <div
                  className="px-3 py-2 text-muted"
                  style={{ fontSize: "0.82rem", backgroundColor: "#fbfcfd" }}
                >
                  <i className="fa fa-arrows me-1"></i>
                  Drag rows using the handle to reorder how testimonials appear
                  on the site.
                </div>
              )} */}

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead style={{ backgroundColor: "#eef1f4" }}>
                    <tr>
                      {/* <th style={{ width: "40px" }}></th> */}
                      <th style={{ width: "70px" }}>Logo</th>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Industry</th>
                      <th style={{ minWidth: "260px" }}>Quote</th>
                      <th style={{ width: "90px" }} className="text-center">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {clients.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center text-muted py-4">
                          No testimonials found.
                        </td>
                      </tr>
                    ) : (
                      // clients.map((client, index) => {
                        clients.map((client) => {
                        const isExpanded = !!expandedRows[client.id];
                        // const isDragging = draggedIndex === index;
                        // const isDragOver =
                        //   dragOverIndex === index && draggedIndex !== index;

                        return (
                          <tr
                            key={client.id}
                            // draggable
                            // onDragStart={(e) => handleDragStart(e, index)}
                            // onDragOver={(e) => handleDragOver(e, index)}
                            // onDragLeave={handleDragLeave}
                            // onDrop={(e) => handleDrop(e, index)}
                            // onDragEnd={handleDragEnd}
                            // style={{
                            //   opacity: isDragging ? 0.4 : 1,
                            //   borderTop: isDragOver
                            //     ? "2px solid #4a90e2"
                            //     : undefined,
                            //   backgroundColor: isDragOver
                            //     ? "#f0f7ff"
                            //     : undefined,
                            // }}
                          >
                            {/* <td
                              style={{
                                cursor: savingOrder ? "wait" : "grab",
                                textAlign: "center",
                                color: "#9aa5b1",
                              }}
                              title="Drag to reorder"
                            >
                              <i className="fa fa-arrows"></i>
                            </td> */}
                            <td>
                              <img
                                src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/clients/${client.client_logo}`}
                                alt={client.client_name}
                                width="50"
                                height="50"
                                style={{
                                  borderRadius: "8px",
                                  objectFit: "cover",
                                }}
                              />
                            </td>

                            <td>{client.client_name}</td>
                            <td>{client.company_name}</td>
                            <td>{client.industry}</td>

                            <td style={{ maxWidth: "320px" }}>
                              <p
                                className="mb-1"
                                style={
                                  isExpanded
                                    ? { margin: 0 }
                                    : {
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        margin: 0,
                                      }
                                }
                              >
                                {client.client_quote}
                              </p>
                              {client.client_quote &&
                                client.client_quote.length > 120 && (
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    onClick={() => toggleExpand(client.id)}
                                  >
                                    {isExpanded ? "Read less" : "Read more"}
                                  </button>
                                )}
                            </td>

                            <td>
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  className="btn btn-warning btn-sm"
                                  title="Edit"
                                  onClick={() => openEditModal(client)}
                                >
                                  <i className="fa fa-pencil"></i>
                                  <span className="visually-hidden">Edit</span>
                                </button>

                                <button
                                  className="btn btn-danger btn-sm"
                                  title="Delete"
                                  onClick={() => deleteClient(client.id)}
                                >
                                  <i className="fa fa-trash"></i>
                                  <span className="visually-hidden">
                                    Delete
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ================= ADD / EDIT MODAL ================= */}
        {showModal && (
          <div
            onClick={closeModal}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1050,
              padding: "20px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "#fff",
                borderRadius: "12px",
                width: "100%",
                maxWidth: "600px",
                maxHeight: "80vh",
                overflowY: "auto",
                boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center px-4 py-3"
                style={{
                  borderBottom: "1px solid #eee",
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                }}
              >
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">
                      {editingId ? "Update Testimonial" : "Add New Testimonial"}
                    </h5>

                    {editingId && (
                      <div className="small text-muted">
                        <div>
                          <strong>Created:</strong>{" "}
                          {selectedTestimonial?.created_at
                            ? new Date(
                                selectedTestimonial.created_at,
                              ).toLocaleString()
                            : "-"}
                        </div>
                        <div>
                          <strong>Last Updated:</strong>{" "}
                          {selectedTestimonial?.updated_at
                            ? new Date(
                                selectedTestimonial.updated_at,
                              ).toLocaleString()
                            : "-"}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="btn btn-sm"
                    onClick={closeModal}
                    title="Close"
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "1.2rem",
                    }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
              </div>

              <div className="p-4" ref={formRef}>
                <div className="mb-3">
                  <label>Client Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.clientName ? "is-invalid" : ""}`}
                    value={clientName}
                    onChange={(e) => {
                      setClientName(e.target.value);
                      clearFieldError("clientName");
                    }}
                  />
                  {errors.clientName && (
                    <div className="text-danger small mt-1">
                      {errors.clientName}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Role / Designation</label>
                  <input
                    type="text"
                    className={`form-control ${errors.clientRole ? "is-invalid" : ""}`}
                    value={clientRole}
                    onChange={(e) => {
                      setClientRole(e.target.value);
                      clearFieldError("clientRole");
                    }}
                  />
                  {errors.clientRole && (
                    <div className="text-danger small mt-1">
                      {errors.clientRole}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Company Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.companyName ? "is-invalid" : ""}`}
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      clearFieldError("companyName");
                    }}
                  />
                  {errors.companyName && (
                    <div className="text-danger small mt-1">
                      {errors.companyName}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Industry</label>
                  <input
                    type="text"
                    className={`form-control ${errors.industry ? "is-invalid" : ""}`}
                    value={industry}
                    onChange={(e) => {
                      setIndustry(e.target.value);
                      clearFieldError("industry");
                    }}
                  />
                  {errors.industry && (
                    <div className="text-danger small mt-1">
                      {errors.industry}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <label>Testimonial</label>
                  <textarea
                    rows="5"
                    maxLength={MAX_QUOTE_LENGTH}
                    className={`form-control ${errors.clientQuote ? "is-invalid" : ""}`}
                    value={clientQuote}
                    onChange={(e) => {
                      setClientQuote(e.target.value);
                      clearFieldError("clientQuote");
                    }}
                  />
                  <div
                    className="text-end mt-1"
                    style={{
                      fontSize: "13px",
                      color:
                        clientQuote.length >= MAX_QUOTE_LENGTH
                          ? "#dc3545"
                          : "#9aa6b2",
                    }}
                  >
                    {clientQuote.length} / {MAX_QUOTE_LENGTH} characters
                  </div>
                  {errors.clientQuote && (
                    <div className="text-danger small mt-1">
                      {errors.clientQuote}
                    </div>
                  )}
                </div>

                {clientLogo ? (
                  <div className="text-center mb-3">
                    <img
                      src={URL.createObjectURL(clientLogo)}
                      alt="Preview"
                      width={220}
                      style={{
                        borderRadius: "10px",
                        maxWidth: "100%",
                      }}
                    />
                  </div>
                ) : (
                  editingId &&
                  currentLogo && (
                    <div className="text-center mb-3">
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}php/uploads/clients/${currentLogo}`}
                        alt="Current Logo"
                        width={320}
                        style={{
                          border: "2px dashed #009bd4",
                          padding: "14px",
                          borderRadius: "10px",
                          maxWidth: "100%",
                        }}
                      />
                    </div>
                  )
                )}

                <div className="mb-3">
                  <label>Client Logo</label>
                  <input
                    id="clientLogo"
                    type="file"
                    className={`form-control ${errors.clientLogo ? "is-invalid" : ""}`}
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(e) => {
                      if (e.target.files.length > 0) {
                        setClientLogo(e.target.files[0]);
                        clearFieldError("clientLogo");
                      }
                    }}
                  />
                  {errors.clientLogo && (
                    <div className="text-danger small mt-1">
                      {errors.clientLogo}
                    </div>
                  )}
                </div>

                <div className="d-flex gap-2 justify-content-center">
                  <button
                    className="site-button px-4 py-2"
                    disabled={loading}
                    onClick={editingId ? updateClient : uploadClient}
                  >
                    {loading
                      ? "Uploading..."
                      : editingId
                        ? "Update Testimonial"
                        : "Upload Testimonial"}
                  </button>

                  <button
                    className="site-button bg-secondary px-4 py-2"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <ToastContainer />
      </div>
    </>
  );
};

export default ManageClients;
